import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Keeps the admin shell for accounts that have an admin surface, and routes a
 * member-only account to the member portal instead.
 *
 * ⚠️ This sits on the SHELL route, deliberately, not on a single child. The
 * landing page's own check only covers `path: '' , pathMatch: 'full'`, so a
 * member holding a restored session who refreshed any other admin URL —
 * /dashboard, /error?reason=no-access, an emailed deep link — re-entered the
 * shell with no portal check at all. That is what a real member hit in prod on
 * `/error?reason=no-access`: admin chrome, admin nav, badged "Member Admin".
 *
 * Runs AFTER authGuard (order in the route's canActivate array), so an
 * unauthenticated visitor is already redirected by then; the null-user branch
 * below is defensive rather than load-bearing.
 *
 * Deliberately does NOT await initReady(): the decision reads orgRoleAssignments
 * straight off the restored session and never touches AccessControlService, so
 * waiting on role sync would delay every admin navigation to answer a question
 * the session alone already settles.
 */
export const staffAccessGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const user = authService.currentUser();
  if (!user) return true;

  // Staff, chapter admins, platform users and dual-access members all belong here.
  if (authService.hasAdminSurface(user)) return true;

  const orgId = authService.resolveMemberPortalTargetOrgId(user);
  if (orgId) {
    // /switch-portal is declared OUTSIDE this shell, so redirecting there cannot
    // re-enter this guard — that is what keeps this from looping. The interstitial
    // owns the handoff, its own error state, and Cancel (which signs them out).
    return router.createUrlTree(['/switch-portal'], { queryParams: { orgId } });
  }

  // No admin surface AND no member org: there is no portal to send them to, so
  // leaving them signed in would strand them on a shell they can't use. Signing
  // out is terminal, which also means it cannot bounce.
  authService.logout();
  return false;
};
